import { GridOptionsWrapper } from 'ag-grid-community';
import { TranslateService } from '@ngx-translate/core';

/**
 * ag-Grid renders its own chrome — the "No Rows To Show" overlay, the pagination
 * bar, the column menu and the filter panels — from an internal locale table that
 * none of our column definitions can reach. Every grid in the app resolves those
 * strings through GridOptionsWrapper.getLocaleTextFunc(), so wrapping that single
 * method localises all of them at once instead of touching each grid.
 *
 * Lookups go to assets/i18n/<lang>.json under an "AgGrid" prefix (AgGridNoRowsToShow,
 * AgGridPage, ...). Anything without a tag keeps ag-Grid's own English default, and a
 * grid that supplies its own localeText/localeTextFunc still wins.
 */

const liveWrappers = new Set<any>();
let installed = false;

export function installAgGridLocalisation(translate: TranslateService): void {
  if (installed || !translate) {
    return;
  }
  installed = true;

  const proto: any = (GridOptionsWrapper as any).prototype;

  const originalGetLocaleTextFunc = proto.getLocaleTextFunc;
  proto.getLocaleTextFunc = function (): (key: string, defaultValue: string) => string {
    const gridLocaleFunc = originalGetLocaleTextFunc.call(this);
    return (key: string, defaultValue: string) => {
      const fromGrid = gridLocaleFunc(key, defaultValue);
      // A grid that configured its own localeText keeps it.
      if (fromGrid !== undefined && fromGrid !== null && fromGrid !== defaultValue) {
        return fromGrid;
      }
      return translateLocaleKey(translate, key, defaultValue);
    };
  };

  // Track live grids so a language switch can repaint text that is already on screen.
  const originalInit = proto.init;
  if (typeof originalInit === 'function') {
    proto.init = function (): any {
      liveWrappers.add(this);
      return originalInit.apply(this, arguments);
    };
  }
  const originalDestroy = proto.destroy;
  if (typeof originalDestroy === 'function') {
    proto.destroy = function (): any {
      liveWrappers.delete(this);
      return originalDestroy.apply(this, arguments);
    };
  }

  translate.onLangChange.subscribe(() => redrawGrids());
  translate.onTranslationChange.subscribe(() => redrawGrids());
}

function translateLocaleKey(translate: TranslateService, key: string, defaultValue: string): string {
  const candidates = ['AgGrid' + toTag(key), 'AgGrid' + toTag(defaultValue)];
  for (const candidate of candidates) {
    if (!candidate || candidate === 'AgGrid') {
      continue;
    }
    const translated = translate.instant(candidate);
    if (translated && translated !== candidate) {
      return translated;
    }
  }
  return defaultValue;
}

function toTag(text: string): string {
  return (text || '')
    .split(/[^A-Za-z0-9]+/)
    .filter((word) => !!word)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
}

// The overlay and the pagination bar cache their rendered text, so they stay in the
// previous language until something forces a repaint.
function redrawGrids(): void {
  liveWrappers.forEach((wrapper: any) => {
    let api: any = null;
    try {
      api = typeof wrapper.getApi === 'function' ? wrapper.getApi() : null;
    } catch (error) {
      api = null;
    }
    if (!api) {
      return;
    }
    try {
      api.refreshHeader();
      api.refreshCells({ force: true });
      if (api.getDisplayedRowCount() === 0) {
        api.hideOverlay();
        api.showNoRowsOverlay();
      }
    } catch (error) {
      // The grid was destroyed between the language change and this repaint.
      liveWrappers.delete(wrapper);
    }
  });
}
